import { useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Linkedin, Twitter } from "lucide-react";
import { LanguageContext } from "@/i18n";
import { BLOG_POSTS, BLOG_UI } from "@/blog/posts";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

export default function BlogPost() {
  const { lang, t } = useContext(LanguageContext);
  const { slug } = useParams();
  const navigate = useNavigate();
  const ui = BLOG_UI[lang] || BLOG_UI.en;
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to={`/${lang}/blog`} replace />;
  const c = post.langs[lang] || post.langs.en;

  const goContact = () => {
    navigate(`/${lang}`);
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    }, 400);
  };

  return (
    <main data-testid="blog-post" className="pt-36 pb-28 min-h-screen">
      <Seo lang={lang} t={t} title={`${c.title} | DADABIO`} desc={c.excerpt} path={`/${lang}/blog/${post.slug}`} />
      <article className="max-w-3xl mx-auto px-6">
        <motion.button
          data-testid="blog-back-button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          onClick={() => navigate(`/${lang}/blog`)}
          className="flex items-center gap-2 text-[13px] font-medium text-zinc-500 hover:text-[#00F0FF] transition-colors duration-300 mb-10"
        >
          <ArrowLeft size={14} className="rtl:-scale-x-100" />
          {ui.back}
        </motion.button>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-[11px] tracking-[0.25em] text-[#00F0FF] mb-5"
        >
          {post.date}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: EASE }}
          className="font-display font-black tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mb-10"
        >
          {c.title}
        </motion.h1>

        <motion.img
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          src={post.img}
          alt={c.title}
          className="w-full h-64 md:h-80 object-cover rounded-2xl border border-white/10 mb-12"
        />

        {c.body.map((p, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 + i * 0.1, ease: EASE }}
            className="text-base md:text-lg font-light text-zinc-400 leading-relaxed mb-7"
          >
            {p}
          </motion.p>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
          className="mt-12 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-500">{ui.share}</span>
          <a
            data-testid="blog-share-linkedin"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://dadabio.com/${lang}/blog/${post.slug}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-zinc-400 hover:text-[#00F0FF] hover:border-[#00F0FF]/60 transition-colors duration-300"
            aria-label="LinkedIn"
          >
            <Linkedin size={16} />
          </a>
          <a
            data-testid="blog-share-x"
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://dadabio.com/${lang}/blog/${post.slug}`)}&text=${encodeURIComponent(c.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-zinc-400 hover:text-[#00F0FF] hover:border-[#00F0FF]/60 transition-colors duration-300"
            aria-label="X"
          >
            <Twitter size={16} />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-14 rounded-2xl border border-[#00F0FF]/25 bg-[#121212] p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
        >
          <p className="font-display font-bold text-xl text-white leading-snug">{t.contact.heading}</p>
          <button
            data-testid="blog-cta-button"
            onClick={goContact}
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#00F0FF] text-black text-sm font-semibold hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] transition-shadow duration-300"
          >
            {t.nav.cta}
            <ArrowUpRight size={15} className="rtl:-scale-x-100" />
          </button>
        </motion.div>
      </article>
    </main>
  );
}
